import {
  QAlert,
  QBox,
  QButton,
  QCenter,
  QModal,
  QModalActions,
  QModalBody,
  QModalHeader,
  QSpinner,
  QText,
  useFeatureFlag,
  useToastProvider,
} from '@qualio/ui-components';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from 'react-query';
import { documentApi } from '../../../api/document';
import {
  DOCMENT_TEMPLATE_UPGRADE,
  DOCMENT_TEMPLATE_UPGRADE_PREVIEW,
  logCustomError,
} from '../../../messages/LogErrorMessages';
import styles from '../DocumentOverview/DocumentEditor/DocumentEditor.module.css';
import { HTMLViewer } from '../DocumentOverview/HTMLViewer/index';

export interface DocumentUpgradePreviewModalProps {
  documentId: number;
  companyId: number;
  targetTemplate: { id: number; version: number };
  currentTemplate: { id: number; version: number };
  onSuccess: () => void;
  onClose: () => void;
}

export const DocumentUpgradePreviewModal = ({
  documentId,
  companyId,
  targetTemplate,
  currentTemplate,
  onSuccess,
  onClose,
}: DocumentUpgradePreviewModalProps) => {
  const { t } = useTranslation();
  const { showToast } = useToastProvider();
  const [isUpgradingDoc, setIsUpgradingDoc] = useState(false);
  const flags = useFeatureFlag();
  const useNewTemplateUpgrade = Boolean(flags['new-template-upgrade']);

  const commonToastProps = {
    id: 'upgrade-document-toast',
    replace: true,
  };

  const createDocumentPreview = () =>
    documentApi.createDocumentUpgradePreview(
      companyId,
      documentId,
      targetTemplate.id,
      useNewTemplateUpgrade,
    );

  const upgradeDocument = async () => {
    setIsUpgradingDoc(true);
    try {
      await documentApi.upgradeDocument(
        companyId,
        documentId,
        targetTemplate.id,
        useNewTemplateUpgrade,
      );
      showToast({
        'data-cy': 'document-upgrade-preview-modal-notify-success-toast',
        ...commonToastProps,
        status: 'success',
        title: t('notifications.templateChangesApplied'),
        description: t('convertFormat.documentUpgradedSuccessfully', {
          currentVersion: currentTemplate.version,
          targetVersion: targetTemplate.version,
        }),
      });
      onSuccess();
      setIsUpgradingDoc(false);
    } catch (error) {
      logCustomError(DOCMENT_TEMPLATE_UPGRADE, { error });
      showToast({
        'data-cy': 'document-upgrade-preview-modal-notify-error-toast',
        ...commonToastProps,
        status: 'error',
        title: t('notifications.error'),
        description: t('convertFormat.failedToApplyTemplateChanges'),
      });
      setIsUpgradingDoc(false);
    }
  };

  const { isLoading: isGeneratingPreview, data: previewItems } = useQuery({
    queryFn: createDocumentPreview,
    queryKey: ['documentPreview', companyId, documentId, targetTemplate.id],
    onError: (error) => {
      logCustomError(DOCMENT_TEMPLATE_UPGRADE_PREVIEW, { error });
      showToast({
        'data-cy': 'document-upgrade-preview-modal-notify-error-toast',
        ...commonToastProps,
        title: t('notifications.error'),
        description: t('convertFormat.couldNotGeneratePreview'),
        status: 'error',
      });
    },
    refetchOnWindowFocus: false,
  });

  // the last element in the list includes the resulting document when upgrading to the latest version
  const previewedContent =
    previewItems?.preview_result_per_version_change[
      previewItems?.preview_result_per_version_change.length - 1
    ];

  return (
    <QModal size="2xl" isOpen onClose={onClose}>
      <QModalHeader>
        <QText>{t('additionalMessages.reviewChangesPending')}</QText>
      </QModalHeader>
      <QModalBody>
        {isGeneratingPreview && (
          <QCenter pb={4}>
            <QSpinner />
          </QCenter>
        )}
        {!isGeneratingPreview && previewedContent?.sections && (
          <>
            <QBox style={{ marginBottom: '10px' }}>
              <QAlert
                title={t('modals.permanentlyApplyChangesFromTemplate')}
                description={
                  <QText>
                    {t('modals.documentWillBeUpgraded', {
                      currentVersion: currentTemplate.version,
                      targetVersion: targetTemplate.version,
                    })}
                  </QText>
                }
                status="warning"
              />
            </QBox>
            <div
              id="documentHTMLViewerContent"
              data-cy="documentHTMLViewerContent"
              className={`${styles['document-editor__document__section-content']} document non-editable-mode section-reset`}
            >
              <HTMLViewer
                sections={previewedContent.sections}
                showSectionTitles={
                  previewedContent.document.text_section_titles_visible
                }
              />
            </div>
          </>
        )}
      </QModalBody>
      <QModalActions>
        <QButton
          isDisabled={isGeneratingPreview || isUpgradingDoc}
          variant="outline"
          onClick={onClose}
        >
          {t('buttons.cancel')}
        </QButton>
        <QButton
          isDisabled={isGeneratingPreview}
          isLoading={isUpgradingDoc}
          onClick={upgradeDocument}
          data-cy="apply-template-changes"
        >
          {t('buttons.apply')}
        </QButton>
      </QModalActions>
    </QModal>
  );
};
